import type React from "react";
import type { ReactNode } from "react";

export type DataGridCellAddr = {
	rowId: string;
	colId: string;
};

/** Per-scope text/typography axes. Applied to the sticky label and/or value cells via fan-out. */
export type DataGridRowFormatAxes = {
	weight?: "normal" | "light" | "semibold";
	tone?: "primary" | "secondary" | "tertiary";
	/** "caption" = text-xs uppercase tracking-wide */
	labelStyle?: "default" | "caption";
	italic?: boolean;
};

/** Token-backed row styling. Base axes fan out to label + cells; `label` / `cells` override per scope.
 *  Container-only axes (`surface`, `density`, `topBorder`) never fan out as text classes. */
export type DataGridRowFormat = DataGridRowFormatAxes & {
	surface?: "base" | "light";
	density?: "default" | "compact";
	topBorder?: boolean;
	label?: DataGridRowFormatAxes;
	cells?: DataGridRowFormatAxes;
};

/** Visual weight of the grid lines. "strong" darkens every cell border and draws an
 *  outer border around the whole table; "default" keeps the lighter subtle/default tokens. */
export type DataGridGridLines = "default" | "strong";

/** Left padding of the sticky label cell at depth 0. "compact" tightens it for flat/dense
 *  grids; "default" keeps the standard depth-0 inset. */
export type DataGridLabelIndent = "default" | "compact";

export type DataGridRow<TData = unknown> = {
	id: string;
	label: string;
	format?: DataGridRowFormat;
	navigable?: boolean;
	hasError?: boolean;
	children?: DataGridRow<TData>[];
	alwaysExpanded?: boolean;
	hideValuesWhenExpanded?: boolean;
	/** Shifts the visual indentation of this row's label without changing its tree
	 *  position or its children's depth. Negative values out-dent (e.g. a subtotal
	 *  that should align with its parent header). */
	indentOffset?: number;
	/** Omits this non-collapsible row's fixed expansion-chevron spacer, out-denting its
	 *  label by the spacer width (e.g. a category label that should sit left of its metrics).
	 *  ORs with the grid-wide `labelColumn.hideExpansionSpacer`. */
	hideExpansionSpacer?: boolean;
	/** Renders this row in the sticky header block, below the column headers, so it stays
	 *  visible while the other rows scroll. Children of a pinned row stay in that block. */
	pinToTop?: boolean;
	/** Rendered before the label text in the sticky label column (e.g. a carrier logo or status mark). */
	labelLeading?: ReactNode;
	/** Replaces the label text node in the sticky label column (e.g. an inline rename input),
	 *  while keeping the chevron, leading node, label actions, and the normal value cells. */
	renderLabel?: () => ReactNode;
	labelActions?: ReactNode;
	/** Render function that replaces per-column cell rendering for this row,
	 *  while keeping the grid-managed sticky label cell. The returned nodes flow
	 *  into the period tracks of the row's grid-template-columns. */
	renderCells?: () => ReactNode;
	/** Render function that replaces the ENTIRE row body, including the sticky
	 *  label cell. The returned nodes are placed directly as grid items, so the
	 *  first node must be the label-column cell. Takes precedence over
	 *  renderCells. Use when the row needs to own the label column too (e.g. an
	 *  inline create form). */
	renderRowBody?: () => ReactNode;
	data?: TData;
};

type BaseColumnDef<TData = unknown> = {
	id: string;
	label: string;
	width: string;
	navigable?: boolean;
	visible?: boolean;
	/** Custom node rendered in the column's header cell in place of the label text. */
	renderHeader?: ReactNode;
	renderCell: (row: DataGridRow<TData>) => ReactNode;
};

export type ReadonlyColumnDef<TData = unknown> = BaseColumnDef<TData> & {
	editable?: false;
};

export type EditableColumnDef<TData = unknown> = BaseColumnDef<TData> & {
	/** Pass true for all rows or a predicate for per-row control. */
	editable: true | ((row: DataGridRow<TData>) => boolean);
	getEditValue: (row: DataGridRow<TData>) => string;
	onCommit: (row: DataGridRow<TData>, draft: string) => void;
	/** Fired when the user presses Escape on the cell (while focused or editing). Use to abandon an in-progress edit or dismiss a cell-level error without committing. */
	onCancel?: (row: DataGridRow<TData>) => void;
	/** Placeholder shown in the edit input. */
	inputPlaceholder?: string;
	/** Input mode for the edit input. Defaults to "text". */
	inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
};

export type ColumnDef<TData = unknown> = ReadonlyColumnDef<TData> | EditableColumnDef<TData>;

export const isEditableColumnDef = <TData = unknown>(
	col: ColumnDef<TData>,
): col is EditableColumnDef<TData> => col.editable !== undefined && col.editable !== false;

export type ColumnGroupDef<TData = unknown> = {
	id: string;
	label: string;
	subLabel?: string;
	/** Custom renderer for the group's top-header cell. Defaults to label + subLabel text. */
	renderGroupHeader?: (context: { groupId: string }) => ReactNode;
	columns: ColumnDef<TData>[];
};

export type DataGridColumnEntry<TData = unknown> = ColumnDef<TData> | ColumnGroupDef<TData>;

export const isColumnGroup = <TData = unknown>(
	entry: DataGridColumnEntry<TData>,
): entry is ColumnGroupDef<TData> => "columns" in entry;

export const getVisibleColumnDefs = <TData = unknown>(
	entries: DataGridColumnEntry<TData>[],
): ColumnDef<TData>[] =>
	entries.flatMap((e) =>
		isColumnGroup(e)
			? e.columns.filter((c) => c.visible !== false)
			: e.visible !== false
				? [e]
				: [],
	);

/** IDs of the last visible column in each group — i.e. the columns whose right
 *  border separates one group (period) from the next. */
export const getGroupBoundaryColumnIds = <TData = unknown>(
	entries: DataGridColumnEntry<TData>[],
): Set<string> => {
	const ids = new Set<string>();
	for (const entry of entries) {
		if (!isColumnGroup(entry)) continue;
		const visibleCols = entry.columns.filter((c) => c.visible !== false);
		const lastCol = visibleCols[visibleCols.length - 1];
		if (lastCol) ids.add(lastCol.id);
	}
	return ids;
};

const EMPTY_BOUNDARY_IDS: ReadonlySet<string> = new Set();

/** Single source of truth for grid-line border classes, shared by the header and every row so
 *  the two can never drift. `cellBorderClass` resolves a column's right border: none on the last
 *  column, the heavier group border on a group boundary, the lighter inner border elsewhere. */
export const gridLineClasses = (
	gridLines: DataGridGridLines,
	groupBoundaryColumnIds: ReadonlySet<string> = EMPTY_BOUNDARY_IDS,
	lastColumnId?: string,
) => {
	const innerBorder = gridLines === "strong" ? "border-bc-strong" : "border-bc-default";
	const groupBorder = "border-bc-strong";
	const groupBoundaryRightBorder = `border-r-2 ${groupBorder}`;
	return {
		innerBorder,
		groupBorder,
		outerBorder: gridLines === "strong" ? "border border-bc-strong rounded-md" : "",
		cellBorderClass: (colId: string) =>
			colId === lastColumnId
				? ""
				: groupBoundaryColumnIds.has(colId)
					? groupBoundaryRightBorder
					: `border-r ${innerBorder}`,
	};
};
